import { Modal } from "flowbite-react";
import { PiggyBank, Rocket } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { StepDots } from "@/features/sponsorship/components/onboarding/StepDots";
import { WizardHeader } from "@/features/sponsorship/components/onboarding/WizardHeader";
import { ThreeStepPath } from "@/features/sponsorship/components/overview/ThreeStepPath";
import {
  useSponsorshipOnboarding,
  type SponsorshipOnboardingStep,
} from "@/features/sponsorship/hooks/useSponsorshipOnboarding";
import { H3 } from "@/shared/ui/typographies/h3";
import { P } from "@/shared/ui/typographies/paragraph";

const STEP_VARIANTS = {
  initial: { opacity: 0, x: 16 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -16 },
} as const;

const STEP_TRANSITION = { duration: 0.2, ease: "easeOut" } as const;

const PromiseStep = () => (
  <div className="flex flex-col items-center gap-3 text-center">
    <span className="inline-flex size-12 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-300">
      <PiggyBank size={24} aria-hidden />
    </span>
    <H3 className="text-gray-900 dark:text-white">Parrainez, c&apos;est gagné</H3>
    <P className="max-w-md text-gray-600 dark:text-gray-300">
      Recommandez Yeati au CSE d&apos;une autre entreprise et cumulez des points à chaque
      étape — même si ça n&apos;aboutit pas, vos points restent acquis.
    </P>
  </div>
);

const MechanicsStep = () => (
  <div className="flex flex-col gap-4">
    <H3 className="text-center text-gray-900 dark:text-white">Comment gagner ?</H3>
    <ThreeStepPath />
    <P size="sm" className="text-center text-gray-600 dark:text-gray-300">
      1 000 points = 10 €, convertibles quand vous voulez.
    </P>
  </div>
);

const CallToActionStep = () => (
  <div className="flex flex-col items-center gap-3 text-center">
    <span className="inline-flex size-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300">
      <Rocket size={24} aria-hidden />
    </span>
    <H3 className="text-gray-900 dark:text-white">Prêt·e à recommander ?</H3>
    <P className="max-w-md text-gray-600 dark:text-gray-300">
      Retrouvez votre progression à tout moment dans l&apos;onglet Aperçu.
      Bonne chasse aux points !
    </P>
  </div>
);

const renderStepContent = (step: SponsorshipOnboardingStep) => {
  switch (step) {
    case 1:
      return <PromiseStep />;
    case 2:
      return <MechanicsStep />;
    case 3:
      return <CallToActionStep />;
  }
};

type SponsorshipOnboardingWizardProps = { hasRecommendations: boolean };

export const SponsorshipOnboardingWizard = ({ hasRecommendations }: SponsorshipOnboardingWizardProps) => {
  const {
    shouldShow, currentStep, totalSteps, next, complete,
  } = useSponsorshipOnboarding(hasRecommendations);

  const isLastStep = currentStep === totalSteps;
  const handlePrimary = () => {
    if (isLastStep) {
      complete();
      return;
    }
    next();
  };

  return (
    <Modal show={shouldShow} onClose={complete} dismissible={false} size="3xl">
      <div className="flex flex-col overflow-hidden rounded-lg">
        <WizardHeader onClose={complete} />
        <div className="flex max-h-[60vh] flex-col gap-6 overflow-y-auto bg-white p-6 dark:bg-gray-800 md:p-8">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={currentStep}
              variants={STEP_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={STEP_TRANSITION}
            >
              {renderStepContent(currentStep)}
            </motion.div>
          </AnimatePresence>
          <div className="flex flex-col items-center gap-4 pt-2">
            <StepDots current={currentStep} total={totalSteps} />
            <button
              type="button"
              onClick={handlePrimary}
              className="rounded-full bg-violet-600 px-8 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-600 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              {isLastStep ? "C'est parti" : "Continuer"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

SponsorshipOnboardingWizard.displayName = "SponsorshipOnboardingWizard";
